// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { toast } from '../components/Toast';
import { setSentryUser } from '../utils/sentry';
import { trackSession, updateSessionActivity } from '../services/sessions.service';

const AuthContext = createContext({});

// Helper to check if user needs to resubscribe (for welcome toast suppression)
const checkNeedsResubscribe = async (userId) => {
  try {
    const { data } = await supabase
      .from('subscriptions')
      .select('status')
      .eq('user_id', userId)
      .maybeSingle();

    if (data) {
      // Has subscription record - check status
      return ['canceled', 'past_due', 'expired'].includes(data.status);
    }
    // No subscription record = free user, no resubscribe needed
    return false;
  } catch {
    return false; // On error, allow welcome toast
  }
};

export const useAuth = () => useContext(AuthContext);

// Returns true when the current session was established via a third-party
// OAuth provider (Discord, Google, etc.) rather than email/password. We
// treat OAuth identity as sufficient — the provider has already verified
// the user, so we skip the 2FA challenge that would normally gate AAL2.
// This is the standard SaaS pattern.
//
// Three layered checks because Supabase's identity tracking is messy when
// accounts are linked (e.g., user signed up with email, later linked
// Discord). app_metadata.provider often reflects the original sign-up,
// NOT the current session, so we also look at:
//   1. The JWT's amr claim — most reliable, but requires the session.
//   2. user.identities ordered by last_sign_in_at — what was used most
//      recently across all linked providers.
//   3. user.app_metadata.provider — fallback for fresh OAuth-only users.
const NON_OAUTH_PROVIDERS = new Set(['email', 'phone']);

const isOAuthUser = (user, session) => {
  if (!user) return false;

  // 1) JWT amr claim — set by GoTrue on the current access token.
  //    Format: [{ method: 'oauth' | 'password' | 'sso' | ..., timestamp: ..., provider: 'discord' }]
  //
  // This is the ONLY signal that reflects how the CURRENT session was
  // established. If amr is present, we trust it and bail out — checks 2
  // and 3 are only for the legacy case where amr isn't in the token
  // (very old sessions, certain test environments).
  //
  // Bug this guards against: a user signs up via Google, later sets a
  // password, then signs in with email+password. Their amr says
  // 'password' but app_metadata.provider still says 'google' and the
  // Google identity is still attached. Without this short-circuit, the
  // fallback checks would see Google and incorrectly mark the session
  // as OAuth — bypassing the 2FA prompt that protects password logins.
  const accessToken = session?.access_token;
  if (accessToken && typeof accessToken === 'string') {
    try {
      const parts = accessToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        const amr = payload?.amr;
        if (Array.isArray(amr) && amr.length > 0) {
          // Trust amr completely when present. Any OAuth/SSO entry in
          // amr means this session was established via that flow.
          const matched = amr.some((entry) => {
            if (!entry) return false;
            if (entry.method === 'oauth' || entry.method === 'sso') return true;
            const p = entry.provider;
            return p && !NON_OAUTH_PROVIDERS.has(p);
          });
          // CRITICAL: return both true AND false from here. Don't fall
          // through to the historical-identity checks when amr is
          // authoritative — that's what caused the 2FA bypass.
          return matched;
        }
        // No amr in JWT — fall through to legacy identity checks below.
      }
    } catch (e) {
      console.warn('[isOAuthUser] JWT parse failed:', e.message);
      // malformed token — fall through to legacy checks
    }
  }
  // No access_token — fall through to legacy identity checks below.

  // 2) Legacy fallback (only when amr isn't in the token): most-recent
  //    identity by last_sign_in_at. Handles old sessions where amr
  //    wasn't issued.
  const identities = user?.identities;
  if (Array.isArray(identities) && identities.length > 0) {
    const sorted = [...identities].sort(
      (a, b) => new Date(b?.last_sign_in_at || 0) - new Date(a?.last_sign_in_at || 0)
    );
    const recent = sorted[0]?.provider;
    if (recent && !NON_OAUTH_PROVIDERS.has(recent)) return true;
  }

  // 3) Legacy fallback: app_metadata.provider for vanilla OAuth-only
  //    users with no amr and no identities array.
  const provider = user?.app_metadata?.provider;
  if (provider && !NON_OAUTH_PROVIDERS.has(provider)) return true;

  return false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // MFA state — when user needs to complete 2FA challenge
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaFactorType, setMfaFactorType] = useState(null); // 'totp' or 'phone'
  const [mfaPhone, setMfaPhone] = useState(null); // Phone number for SMS MFA display
  const [mfaChecking, setMfaChecking] = useState(false); // True while checking MFA status

  // Stripe checkout redirect state - prevents dashboard flash during checkout redirect
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);

  // Fetch user profile from profiles table (creates if missing)
  const fetchProfile = async (userId, userEmail = null, userMeta = {}) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.warn('[AuthContext] Profile fetch error:', error.message, error.code);
      }

      if (data) return data;

      // Profile doesn't exist - create it
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: userEmail,
          full_name: userMeta?.full_name || userMeta?.name || null,
          avatar_url: userMeta?.avatar_url || null,
          role: 'user', // Default role for new users
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (createError) {
        console.warn('[AuthContext] Profile creation error:', createError.message, createError.code);
        return null;
      }

      // Also create user_settings if missing
      await supabase
        .from('user_settings')
        .insert({
          user_id: userId,
          use_global_db: true,
          currency: 'USD',
          timezone: 'America/Chicago',
          use_compact_view: false,
        })
        .select()
        .maybeSingle();

      return newProfile;
    } catch (error) {
      console.warn('[AuthContext] fetchProfile unexpected error:', error.message);
      return null;
    }
  };

  // Check if MFA verification is required (supports both TOTP and SMS)
  const checkMfaRequired = async () => {
    setMfaChecking(true);
    try {
      // OAuth users (Discord, Google, etc.) skip the 2FA prompt — the
      // OAuth provider has already verified them, which we accept as a
      // sufficient identity check. 2FA enrolled on the account still
      // protects password logins.
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const isOauth = isOAuthUser(currentSession?.user, currentSession);
      if (isOauth) {
        // OAuth/SSO users skip 2FA — their provider already handled it.
        setMfaRequired(false);
        setMfaFactorId(null);
        setMfaFactorType(null);
        setMfaPhone(null);
        return false;
      }

      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (error) {
        console.error('[AuthContext] MFA assurance check failed:', error.message);
        setMfaRequired(true);
        setMfaFactorId(null);
        setMfaFactorType(null);
        setMfaPhone(null);
        return true;
      }

      if (!data || data.currentLevel === 'aal2') {
        // Already authenticated at AAL2 — nothing more to do.
        setMfaRequired(false);
        setMfaFactorId(null);
        setMfaFactorType(null);
        setMfaPhone(null);
        return false;
      }

      if (data.currentLevel === 'aal1' && data.nextLevel === 'aal2') {
        const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();

        if (factorsError) {
          console.error('[AuthContext] MFA factor list failed:', factorsError.message);
          setMfaRequired(true);
          setMfaFactorId(null);
          setMfaFactorType(null);
          setMfaPhone(null);
          return true;
        }

        const totpFactor = factors?.totp?.find(f => f.status === 'verified');
        if (totpFactor) {
          setMfaRequired(true);
          setMfaFactorId(totpFactor.id);
          setMfaFactorType('totp');
          setMfaPhone(null);
          return true;
        }

        const phoneFactor = factors?.phone?.find(f => f.status === 'verified');
        if (phoneFactor) {
          setMfaRequired(true);
          setMfaFactorId(phoneFactor.id);
          setMfaFactorType('phone');
          setMfaPhone(phoneFactor.phone || null);
          return true;
        }

        // AAL says we need aal2 but no VERIFIED factor exists. This means
        // the user has an unverified (in-progress) enrollment. Treat as
        // "no MFA" so they aren't locked out. The factor needs to be
        // verified-then-completed in Settings to take effect.
        console.warn('[MFA-check] currentLevel=aal1 nextLevel=aal2 but NO VERIFIED FACTOR — user has incomplete enrollment, MFA NOT required');
      }

      setMfaRequired(false);
      setMfaFactorId(null);
      setMfaFactorType(null);
      setMfaPhone(null);
      return false;
    } catch (error) {
      console.error('[AuthContext] MFA check error:', error.message);
      setMfaRequired(true);
      setMfaFactorId(null);
      setMfaFactorType(null);
      setMfaPhone(null);
      return true;
    } finally {
      setMfaChecking(false);
    }
  };

  // Verify MFA code during login (works for both TOTP and SMS)
  const verifyMfa = async (code, options = {}) => {
    const { showToast = false } = options;
    try {
      if (!mfaFactorId) throw new Error('No MFA factor found');

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId
      });

      if (challengeError) throw challengeError;

      const { data, error } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: code
      });

      if (error) throw error;

      setMfaRequired(false);
      setMfaFactorId(null);
      setMfaFactorType(null);
      setMfaPhone(null);

      // Only show welcome toast if user doesn't need to resubscribe and showToast is true
      if (showToast) {
        const needsResub = await checkNeedsResubscribe(data.user?.id);
        if (!needsResub) {
          toast.success('Welcome back!');
        }
      }

      // Track this session/device after MFA verification - disabled until post-launch
      // const { data: { session: currentSession } } = await supabase.auth.getSession();
      // if (currentSession) {
      //   trackSession(currentSession).catch(() => {});
      // }

      return { data, error: null };
    } catch (error) {
      console.error('MFA verification error:', error);
      if (showToast) {
        toast.error(error.message || 'Invalid verification code');
      }
      return { data: null, error };
    }
  };

  // Send SMS challenge (for phone MFA - sends the code)
  const sendMfaSmsChallenge = async () => {
    try {
      if (!mfaFactorId || mfaFactorType !== 'phone') {
        throw new Error('No phone MFA factor found');
      }

      const { data, error } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('SMS challenge error:', error);
      return { data: null, error };
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const timeout = setTimeout(() => {
      if (mounted && loading) {
        setLoading(false);
        setMfaRequired(false);
        setMfaFactorType(null);
        setMfaPhone(null); // Also clear MFA state on timeout
      }
    }, 5000);

    // Handle browser back/forward cache (bfcache) - ensures fresh state on navigation
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - refresh session state
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session) {
            // No valid session - ensure clean state
            setUser(null);
            setSession(null);
            setProfile(null);
            setMfaRequired(false);
            setMfaFactorId(null);
            setMfaFactorType(null);
            setMfaPhone(null);
          }
        });
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    const initializeAuth = async () => {
      try {
        // Detect an in-flight OAuth callback. Supabase processes the URL
        // hash/search async on its own, but during that window getSession()
        // and AAL checks can still return stale data from a previous
        // session. Running the "incomplete MFA → sign out" guard against
        // that stale state is what was kicking Discord-OAuth users with
        // 2FA enrolled — they'd land on /dashboard, AAL would still
        // reflect the prior aal1+aal2-required state, and we'd nuke the
        // brand-new OAuth session before it had a chance to settle.
        const url = typeof window !== 'undefined' ? window.location : null;
        const isOAuthCallback = !!url && (
          (url.hash && /access_token=|provider_token=|[#&]error=/.test(url.hash)) ||
          (url.search && /[?&](code|state|error)=/.test(url.search))
        );

        // On page refresh/load, check if user has incomplete MFA session.
        // If they have aal1 but need aal2 AND they're a password user, that
        // means they refreshed mid-2FA-challenge → sign them out to force
        // fresh login. OAuth users (Discord/Google/etc.) also sit at AAL1
        // but should NOT be kicked — their provider already verified them
        // and we accept that as sufficient.
        if (!isOAuthCallback) {
          const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
            const { data: { session: peekSession } } = await supabase.auth.getSession();
            if (!isOAuthUser(peekSession?.user, peekSession)) {
              // Password user mid-2FA — drop the session.
              await supabase.auth.signOut();
              setMfaRequired(false);
              setMfaFactorId(null);
              setMfaFactorType(null);
              setMfaPhone(null);
              setUser(null);
              setSession(null);
              setProfile(null);
              if (mounted) setLoading(false);
              toast.warning('Session timed out. Please sign in again.');
              return;
            }
            // OAuth user — fall through, we'll skip the 2FA prompt below.
          }
        }

        // Clear any stale MFA UI state
        setMfaRequired(false);
        setMfaFactorId(null);
        setMfaFactorType(null);
        setMfaPhone(null);

        // On mobile, the URL hash might contain auth tokens from email confirmation
        // We need to explicitly parse and set the session from the hash
        const hashParams = window.location.hash;
        if (hashParams && hashParams.includes('access_token')) {
          // Parse the hash to extract tokens
          const params = new URLSearchParams(hashParams.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            // Explicitly set the session from URL tokens (fixes mobile email confirmation)
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (setSessionError) {
              console.warn('[AuthContext] Error setting session from URL:', setSessionError.message);
            } else if (data?.session) {
              // Successfully set session - clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }
        }

        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          if (mounted) setLoading(false);
          return;
        }

        // No session = no user = no MFA needed
        if (!currentSession?.user) {
          if (mounted) {
            setMfaRequired(false);
            setMfaFactorId(null);
            setMfaFactorType(null);
            setMfaPhone(null);
            setMfaChecking(false);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setSession(currentSession);
          setUser(currentSession.user);

          // Set Sentry user context
          setSentryUser(currentSession.user);

          // Check MFA with a timeout to prevent hanging. SECURITY-CRITICAL:
          // do NOT reset mfaRequired on timeout/error here. checkMfaRequired
          // has its own internal try/catch that fail-closes appropriately
          // (sets mfaRequired=true on errors). The previous fail-open catch
          // in this wrapper was unconditionally setting mfaRequired=false
          // after checkMfaRequired had correctly set it to true, completely
          // bypassing the 2FA gate.
          try {
            const mfaCheckPromise = checkMfaRequired();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('MFA check timeout')), 3000)
            );
            await Promise.race([mfaCheckPromise, timeoutPromise]);
          } catch (mfaError) {
            // On timeout: trust whatever state checkMfaRequired already
            // set. Most likely it had already called setMfaRequired(true)
            // by the time we hit the timeout — we must not undo that.
            // If checkMfaRequired truly never got past its first await,
            // its OWN catch sets mfaRequired=true (fail-closed), which is
            // exactly what we want.
            console.warn('[AuthContext] MFA check timed out — preserving state set by checkMfaRequired');
          }

          // Fetch profile in background (don't await)
          fetchProfile(
            currentSession.user.id,
            currentSession.user.email,
            currentSession.user.user_metadata
          ).then(profileData => {
            if (mounted) setProfile(profileData);
          });

          // Track this session/device on app load - disabled until post-launch
          // trackSession(currentSession).catch(() => {});
        }
      } catch (error) {
        // Auth initialization error - silently handle
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;

        // Clear URL hash after successful auth events (mobile email confirmation fix)
        if (newSession && window.location.hash &&
            (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        // For events that don't change the actual user, preserve object references to prevent re-renders
        // This prevents modals from closing when switching between browser tabs
        // TOKEN_REFRESHED: Token was refreshed (happens on tab focus, timer, etc.)
        // SIGNED_IN: Can fire from another tab via localStorage sync
        // USER_UPDATED: User metadata changed but ID is same
        const preserveReferenceEvents = ['TOKEN_REFRESHED', 'SIGNED_IN', 'USER_UPDATED'];

        if (preserveReferenceEvents.includes(event)) {
          setSession(prev => {
            // Keep same reference if user ID hasn't changed - prevents unnecessary re-renders
            if (prev?.user?.id === newSession?.user?.id) {
              return prev;
            }
            return newSession;
          });
          setUser(prev => {
            if (prev?.id === newSession?.user?.id) {
              return prev; // Keep same reference if user ID hasn't changed
            }
            return newSession?.user ?? null;
          });
          // Don't call setLoading(false) if we already have a session - prevents flash
          if (newSession?.user) {
            setLoading(prev => prev ? false : prev);
          }
        } else {
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
        }

        // Update Sentry user context
        setSentryUser(newSession?.user ?? null);

        // Handle MFA verification success - clear MFA required state
        if (event === 'MFA_CHALLENGE_VERIFIED') {
          setMfaRequired(false);
          setMfaFactorId(null);
          setMfaFactorType(null);
          setMfaPhone(null);
          return; // Don't check MFA again, we just verified it
        }

        if (newSession?.user) {
          // Only check MFA on initial sign in
          if (event === 'SIGNED_IN') {
            // SECURITY: do NOT fail-open in the catch. checkMfaRequired
            // internally fail-closes (sets mfaRequired=true on errors).
            // The previous catch unconditionally set it to false, which
            // could fire AFTER the inner setMfaRequired(true) ran and
            // silently bypass 2FA. Log-only catch preserves whatever the
            // inner function decided.
            checkMfaRequired().catch((err) => {
              console.error('[AuthContext] SIGNED_IN MFA check rejected (state preserved):', err?.message);
            });

            // Track this session/device on sign in - disabled until post-launch
            // trackSession(newSession).catch(() => {});
          }

          // Only fetch profile if user actually changed or on initial sign in
          // Skip for TOKEN_REFRESHED to prevent unnecessary re-renders on tab switch
          if (event !== 'TOKEN_REFRESHED') {
            fetchProfile(
              newSession.user.id,
              newSession.user.email,
              newSession.user.user_metadata
            ).then(profileData => {
              if (mounted) {
                // Only update if profile actually changed
                setProfile(prev => {
                  if (prev?.id === profileData?.id &&
                      prev?.full_name === profileData?.full_name &&
                      prev?.avatar_url === profileData?.avatar_url &&
                      prev?.role === profileData?.role) {
                    return prev; // Keep same reference
                  }
                  return profileData;
                });
              }
            });
          }
        } else {
          setProfile(null);
          setMfaRequired(false);
          setMfaFactorId(null);
          setMfaFactorType(null);
          setMfaPhone(null);
        }
      }
    );

    // Periodically update session activity - disabled until post-launch
    // const activityInterval = setInterval(() => {
    //   if (session) {
    //     updateSessionActivity(session).catch(() => {});
    //   }
    // }, 5 * 60 * 1000);

    return () => {
      mounted = false;
      clearTimeout(timeout);
      // clearInterval(activityInterval);
      subscription.unsubscribe();
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Sign up with email
  const signUp = async (email, password, options = {}) => {
    const { captchaToken, showToast = false, ...metadata } = options;
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          captchaToken, // Pass to Supabase for verification
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) throw error;

      if (data?.user?.identities?.length === 0) {
        return { error: { message: 'An account with this email already exists' } };
      }

      if (showToast) {
        toast.success('Account created! Check your email to confirm.');
      }
      return { data, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      if (showToast) {
        toast.error(error.message || 'Failed to sign up');
      }
      return { data: null, error };
    }
  };

  // Sign in with email
  const signIn = async (email, password, options = {}) => {
    const { captchaToken, showToast = false } = options;
    try {
      // Set mfaChecking early to prevent dashboard flash
      setMfaChecking(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken, // Pass to Supabase for verification
        },
      });

      if (error) {
        setMfaChecking(false);
        throw error;
      }

      const needsMfa = await checkMfaRequired();

      // Only show welcome toast if no MFA needed, user doesn't need to resubscribe, and showToast is true
      if (!needsMfa && showToast) {
        const needsResub = await checkNeedsResubscribe(data.user?.id);
        if (!needsResub) {
          toast.success('Welcome back!');
        }
        // Track this session/device - disabled until post-launch
        // trackSession(data.session).catch(() => {});
      }

      return { data, error: null, mfaRequired: needsMfa };
    } catch (error) {
      console.error('Signin error:', error);
      if (showToast) {
        toast.error(error.message || 'Failed to sign in');
      }
      setMfaChecking(false);
      return { data: null, error };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Google signin error:', error);
      toast.error(error.message || 'Failed to sign in with Google');
      return { data: null, error };
    }
  };

  // Sign in with Apple — web OAuth flow. Requires a Services ID + client
  // secret configured on the Supabase Apple provider (in addition to the
  // native iOS bundle id already listed there).
  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Apple signin error:', error);
      toast.error(error.message || 'Failed to sign in with Apple');
      return { data: null, error };
    }
  };

  // Sign in with Discord
  const signInWithDiscord = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          scopes: 'identify email', // Basic scopes for authentication
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Discord signin error:', error);
      toast.error(error.message || 'Failed to sign in with Discord');
      return { data: null, error };
    }
  };

  // Join Discord server and assign role based on subscription
  const joinDiscordServer = async () => {
    try {
      // Get the current session which contains the provider token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.provider_token) {
        // Provider token not available - user may need to re-authenticate with Discord
        return { success: false, error: 'Discord authentication required. Please sign in with Discord again.' };
      }

      // Call the Edge Function to join the server
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/discord-join-server`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discordAccessToken: session.provider_token,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to join Discord server');
      }

      toast.success(result.alreadyMember
        ? `Welcome back to Discord! You have the ${result.role} role.`
        : `Joined Discord server! You have the ${result.role} role.`
      );

      return { success: true, ...result };
    } catch (error) {
      console.error('Discord join error:', error);
      return { success: false, error: error.message };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Clear MFA state BEFORE signing out to prevent flash on reload
      setMfaRequired(false);
      setMfaFactorId(null);
      setMfaFactorType(null);
      setMfaPhone(null);
      setMfaChecking(false);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear any residual session data from storage
      // This ensures 2FA will be required on next login
      if (typeof window !== 'undefined') {
        window.sessionStorage.clear();
        // Clear all Supabase auth keys from localStorage
        const keysToRemove = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => window.localStorage.removeItem(key));
      }

      setUser(null);
      setProfile(null);
      setSession(null);
      return { error: null };
    } catch (error) {
      console.error('Signout error:', error);
      toast.error(error.message || 'Failed to sign out');
      return { error };
    }
  };

  // Sign out from all devices
  const signOutAll = async () => {
    try {
      // Clear MFA state BEFORE signing out to prevent flash on reload
      setMfaRequired(false);
      setMfaFactorId(null);
      setMfaFactorType(null);
      setMfaPhone(null);
      setMfaChecking(false);

      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;

      // Clear any residual session data from storage
      // This ensures 2FA will be required on next login
      if (typeof window !== 'undefined') {
        window.sessionStorage.clear();
        // Clear all Supabase auth keys from localStorage
        const keysToRemove = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => window.localStorage.removeItem(key));
      }

      setUser(null);
      setProfile(null);
      setSession(null);
      toast.success('Signed out from all devices');
      return { error: null };
    } catch (error) {
      console.error('Global signout error:', error);
      toast.error(error.message || 'Failed to sign out from all devices');
      return { error };
    }
  };

  // Reset password
  const resetPassword = async (email, options = {}) => {
    const { captchaToken, showToast = false } = options;
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
        captchaToken, // Pass to Supabase for verification
      });

      if (error) throw error;

      if (showToast) {
        toast.success('Password reset email sent!');
      }
      return { data, error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      if (showToast) {
        toast.error(error.message || 'Failed to send reset email');
      }
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast.success('Password updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.message || 'Failed to update password');
      return { data: null, error };
    }
  };

  // Update profile
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      // Security: strip sensitive fields that users should never modify directly
      const BLOCKED_FIELDS = ['role', 'id', 'created_at', 'stripe_customer_id'];
      const safeUpdates = { ...updates };
      for (const field of BLOCKED_FIELDS) {
        delete safeUpdates[field];
      }

      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...safeUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      toast.success('Profile updated');
      return { data, error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.message || 'Failed to update profile');
      return { data: null, error };
    }
  };

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);

      const { data: { user: freshUser } } = await supabase.auth.getUser();
      if (freshUser) {
        setUser(freshUser);
      }
    }
  }, [user, fetchProfile]);

  // Get session info
  const getSessionInfo = useCallback(() => {
    if (!session) return null;

    return {
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      expiresAt: session.expires_at,
      expiresIn: session.expires_in,
      tokenType: session.token_type,
      isExpired: session.expires_at ? new Date(session.expires_at * 1000) < new Date() : false,
      expiresAtDate: session.expires_at ? new Date(session.expires_at * 1000) : null,
    };
  }, [session]);

  // Create currentUser object for backwards compatibility (memoized to prevent unnecessary re-renders)
  const currentUser = useMemo(() => {
    if (!user) return null;
    return {
      uid: user.id,
      email: user.email,
      displayName: profile?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || null,
      photoURL: profile?.avatar_url || user.user_metadata?.avatar_url || null,
      emailVerified: user.email_confirmed_at != null,
      created_at: user.created_at, // For welcome modal timing
    };
  }, [user?.id, user?.email, user?.email_confirmed_at, user?.created_at, user?.user_metadata?.full_name, user?.user_metadata?.name, user?.user_metadata?.avatar_url, profile?.full_name, profile?.avatar_url]);

  // Role-based access helpers
  const isAdmin = profile?.role === 'admin';
  const userRole = profile?.role || 'user';

  // Memoize context value to prevent unnecessary re-renders
  // Functions are stable (useCallback) so they won't cause re-renders
  // Only state changes will trigger context updates
  const value = useMemo(() => ({
    // State
    user,
    profile,
    session,
    loading,
    currentUser,

    // Role-based access
    isAdmin,
    userRole,

    // MFA state
    mfaRequired,
    mfaFactorId,
    mfaFactorType,
    mfaPhone,
    mfaChecking,

    // Checkout redirect state
    redirectingToCheckout,
    setRedirectingToCheckout,

    // Auth methods
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInWithDiscord,
    signOut,
    signOutAll,
    resetPassword,
    updatePassword,
    updateProfile,
    refreshUserData,

    // Discord integration
    joinDiscordServer,

    // MFA methods
    verifyMfa,
    checkMfaRequired,
    sendMfaSmsChallenge,

    // Session helpers
    getSessionInfo,
  }), [
    user, profile, session, loading, currentUser,
    isAdmin, userRole,
    mfaRequired, mfaFactorId, mfaFactorType, mfaPhone, mfaChecking,
    redirectingToCheckout, setRedirectingToCheckout,
    signUp, signIn, signInWithGoogle, signInWithApple, signInWithDiscord, signOut, signOutAll,
    resetPassword, updatePassword, updateProfile, refreshUserData, joinDiscordServer,
    verifyMfa, checkMfaRequired, sendMfaSmsChallenge, getSessionInfo
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;